import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Shield, ArrowRight, Lock, CheckCircle, Mic, Scale,
  ShieldCheck, Phone, ChevronDown, BadgeCheck,
} from "lucide-react";

const OUTCOMES = [
  { type: "Car Accident", text: "Attorney contacted within 6 hrs" },
  { type: "Criminal Defense", text: "2 attorneys reached out same day" },
  { type: "Personal Injury", text: "Case reviewed · Attorney matched" },
  { type: "Employment Dispute", text: "Connected in under 12 minutes" },
  { type: "DUI", text: "Attorney called within 4 hrs" },
  { type: "Family Law", text: "3 attorneys responded same day" },
  { type: "Immigration", text: "Matched · Attorney reached out" },
  { type: "Wrongful Termination", text: "Case reviewed · Attorney assigned" },
  { type: "Slip & Fall", text: "Attorney responded within 8 hrs" },
  { type: "Medical Malpractice", text: "2 attorneys reviewed same day" },
];

const FAQS = [
  {
    q: "Is this really free?",
    a: "100% free for anyone seeking legal help. Attorneys pay to access qualified leads — you never pay anything, ever.",
  },
  {
    q: "Who sees my information?",
    a: "Only attorneys matched to your case type and location. Your intake is encrypted and never sold to advertisers or third parties.",
  },
  {
    q: "Will I get spammed with calls?",
    a: "No. Attorneys are vetted before they can access leads. You control the conversation and can opt out at any time.",
  },
  {
    q: "What types of cases qualify?",
    a: "Personal injury, car accidents, criminal defense, DUI, employment disputes, family law, immigration, wrongful termination, and more. If you're unsure, submit anyway — describe your situation and we'll match you with the right attorney.",
  },
  {
    q: "How fast will an attorney reach out?",
    a: "Most clients hear from at least one attorney within 24–48 hours. High-priority cases (accidents, criminal charges) often get same-day contact.",
  },
  {
    q: "What if I don't know if I have a case?",
    a: "That's fine — just describe your situation in your own words. Our team and matched attorneys will help you understand your legal options. No commitment, no pressure.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [consented, setConsented] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const checkboxRef = useRef(null);
  const ctaCardRef = useRef(null);

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!ctaCardRef.current) return;
      setShowSticky(ctaCardRef.current.getBoundingClientRect().bottom < -80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleGatedNav = () => {
    if (consented) { navigate("/intake"); return; }
    checkboxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const label = checkboxRef.current;
    if (label) {
      label.style.outline = "2px solid hsl(var(--primary))";
      label.style.outlineOffset = "4px";
      label.style.borderRadius = "12px";
      setTimeout(() => {
        label.style.outline = "";
        label.style.outlineOffset = "";
      }, 1800);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden relative">
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track { animation: ticker 40s linear infinite; }
        .ticker-track:hover { animation-play-state: paused; }
        @keyframes faq-open {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .faq-body { animation: faq-open 0.18s ease-out both; }
      `}</style>

      <Helmet>
        <title>ICantFindALawyer — Stop Searching for a Lawyer. Let Lawyers Find You.</title>
        <meta name="description" content="Tired of struggling to find a lawyer? Stop searching. Describe your case by voice or text and let qualified attorneys come to you — personal injury, family law, criminal defense, employment and more. 100% free, 24/7, confidential." />
        <link rel="canonical" href="https://icantfindalawyer.com/" />
      </Helmet>

      {/* Background */}
      <div className="hero-glow fixed inset-0 pointer-events-none" />
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />

      {/* Floating legal symbols */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <svg className="absolute opacity-[0.04] animate-float-slow" style={{ top: '8%', left: '5%', width: 120, height: 120 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8">
          <path d="M12 3v18M4.5 7.5l15 0M4.5 7.5L2 14c0 1.5 1.12 3 2.5 3s2.5-1.5 2.5-3L4.5 7.5zM19.5 7.5L17 14c0 1.5 1.12 3 2.5 3s2.5-1.5 2.5-3L19.5 7.5z"/>
        </svg>
        <svg className="absolute opacity-[0.03] animate-float-mid" style={{ top: '65%', right: '8%', width: 160, height: 160 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.6">
          <path d="M12 3v18M4.5 7.5l15 0M4.5 7.5L2 14c0 1.5 1.12 3 2.5 3s2.5-1.5 2.5-3L4.5 7.5zM19.5 7.5L17 14c0 1.5 1.12 3 2.5 3s2.5-1.5 2.5-3L19.5 7.5z"/>
        </svg>
        <svg className="absolute opacity-[0.035] animate-float-reverse" style={{ bottom: '15%', left: '12%', width: 100, height: 100 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8">
          <path d="M14.5 3l-5 5 7 7 5-5-7-7zM5.5 21.5l4-4M2 18l4 4"/>
        </svg>
        <svg className="absolute opacity-[0.03] animate-float-slow" style={{ top: '30%', right: '20%', width: 90, height: 90, animationDelay: '3s' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.7">
          <path d="M12 2l7 4v5c0 5.25-3.5 9.74-7 11-3.5-1.26-7-5.75-7-11V6l7-4z"/>
        </svg>
        <svg className="absolute opacity-[0.025] animate-float-mid" style={{ top: '50%', left: '35%', width: 80, height: 80, animationDelay: '5s' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2"/>
        </svg>
        <div className="absolute top-0 bottom-0 left-[15%] w-px bg-gradient-to-b from-transparent via-primary/[0.04] to-transparent" />
        <div className="absolute top-0 bottom-0 left-[85%] w-px bg-gradient-to-b from-transparent via-primary/[0.04] to-transparent" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent" />
      </div>

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
              <Scale className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold text-base tracking-tight" style={{ fontFamily: 'var(--heading-font)' }}>ICantFindALawyer</span>
          </div>
          <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-terms-link">
            Terms & Privacy
          </Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <main className="relative min-h-screen flex items-center pt-14">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">

            {/* Left — Headline */}
            <div className={`col-span-1 lg:col-span-7 space-y-6 sm:space-y-8 z-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass w-fit" data-testid="voice-highlight">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inset-0 rounded-full bg-primary/60 animate-ping" />
                  <span className="relative rounded-full h-3 w-3 bg-primary" />
                </span>
                <Mic className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs sm:text-sm font-medium text-foreground/80">Where Lawyers Find You</span>
              </div>

              <div>
                <h1
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95]"
                  style={{ fontFamily: 'var(--heading-font)' }}
                  data-testid="hero-headline"
                >
                  Stop Searching.<br />
                  <span className="gradient-text">Get Found.</span>
                </h1>
                <p className="mt-4 sm:mt-5 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed" data-testid="hero-subtitle">
                  You shouldn't have to struggle to find legal help. Tell us your situation — by voice or text — and let the right lawyers come to you. Free, fast, and confidential.
                </p>
              </div>

              <div className="flex flex-wrap gap-5 sm:gap-8">
                {[
                  { icon: ShieldCheck, label: "Encrypted" },
                  { icon: Lock, label: "Confidential" },
                  { icon: Shield, label: "Always Free" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 text-muted-foreground/60">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs sm:text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-8 sm:gap-12 pt-2">
                {[
                  { value: "24/7", label: "Available" },
                  { value: "100%", label: "Free" },
                  { value: "<5min", label: "Avg. Intake" },
                ].map(({ value, label }) => (
                  <div key={label}>
                    <div className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'var(--heading-font)' }}>{value}</div>
                    <div className="text-xs text-muted-foreground/50 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {["Personal Injury", "Family Law", "Criminal Defense", "Employment", "Immigration", "Car Accident", "Divorce", "DUI"].map(area => (
                  <span key={area} className="px-2.5 py-1 rounded-full text-xs bg-secondary/30 text-muted-foreground/60 border border-border/20">{area}</span>
                ))}
              </div>
            </div>

            {/* Right — CTA Card */}
            <div
              ref={ctaCardRef}
              className={`col-span-1 lg:col-span-5 z-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 space-y-5 relative overflow-hidden" data-testid="terms-section">
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold" style={{ fontFamily: 'var(--heading-font)' }}>Tell Us What Happened</h2>
                    <p className="text-xs text-muted-foreground">We'll match you with the right attorney</p>
                  </div>
                </div>

                {/* Terms summary */}
                <div className="space-y-1.5 text-xs text-muted-foreground/70 leading-relaxed">
                  <p className="text-muted-foreground font-medium text-xs">By proceeding you agree:</p>
                  {[
                    "Information collected solely to connect you with attorneys",
                    "No attorney-client relationship created",
                    "AI analysis is informational, not legal advice",
                    "Platform not liable for attorney actions or outcomes",
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-primary/40 mt-1.5 flex-shrink-0" />
                      <span>{t}</span>
                    </div>
                  ))}
                  <Link to="/terms" className="inline-flex items-center gap-1 text-primary text-xs hover:underline mt-1" data-testid="view-full-terms-link">
                    Full terms <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>

                {/* Consent checkbox */}
                <label
                  ref={checkboxRef}
                  className="flex items-start gap-3 p-3 rounded-xl border border-border/40 bg-white/[0.02] cursor-pointer hover:bg-white/[0.04] transition-all duration-200"
                  data-testid="consent-label"
                >
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={consented}
                      onChange={(e) => setConsented(e.target.checked)}
                      className="absolute inset-0 w-5 h-5 opacity-0 cursor-pointer z-10"
                      data-testid="consent-checkbox"
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${consented ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-transparent'}`}>
                      {consented && <CheckCircle className="w-3 h-3 text-primary-foreground" />}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I agree to the <Link to="/terms" className="text-primary hover:underline" onClick={e => e.stopPropagation()}>Terms, Privacy & Disclaimer</Link>
                    </span>
                    <span className="text-[10px] text-muted-foreground/70 leading-relaxed">
                      This platform uses AI (OpenAI/ChatGPT) to process your intake. By proceeding, you acknowledge that your information will be transmitted to third-party AI services and that ICantFindALawyer is not liable for data processed by these services.
                    </span>
                  </div>
                </label>

                {/* CTA */}
                <div className="space-y-2">
                  <button
                    onClick={handleGatedNav}
                    className={`w-full h-12 sm:h-14 rounded-xl font-semibold text-sm sm:text-base flex items-center justify-center gap-2.5 transition-all duration-300 ${
                      consented
                        ? 'bg-primary text-primary-foreground hover:brightness-110 animate-glow-pulse active:scale-[0.98]'
                        : 'bg-secondary/50 text-muted-foreground hover:bg-secondary/70 cursor-pointer'
                    }`}
                    data-testid="start-intake-btn"
                  >
                    {consented
                      ? <><span>Start Free Intake</span><ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" /></>
                      : <span>↑ Check the box above to begin</span>
                    }
                  </button>

                  {/* Live urgency signal */}
                  <div className="flex items-center justify-center gap-2 pt-0.5">
                    <span className="relative flex h-2 w-2 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400/60 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    <span className="text-[11px] text-muted-foreground/55">Attorneys reviewing cases right now · Takes under 5 min</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* ── Outcome Ticker ── */}
      <div className="relative z-10 border-y border-border/20 py-3 overflow-hidden bg-background/40 backdrop-blur-sm">
        <div className="flex ticker-track w-max select-none">
          {[...OUTCOMES, ...OUTCOMES].map((item, i) => (
            <div key={i} className="flex items-center gap-2 mx-8 flex-shrink-0">
              <span className="text-xs font-semibold text-primary/75">{item.type}</span>
              <span className="text-xs text-muted-foreground/40">·</span>
              <span className="text-xs text-muted-foreground/65">{item.text}</span>
              <span className="ml-6 w-px h-3 bg-border/30 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      {/* ── How It Works ── */}
      <section className="relative z-10 py-16 sm:py-24" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium border border-primary/20 bg-primary/5 text-primary mb-4">Simple Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>How It Works</h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">Three steps — no forms, no phone trees, no frustration.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 relative">
            <div className="hidden md:block absolute top-10 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 pointer-events-none" />
            {[
              {
                num: "01",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-primary">
                    <path d="M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13z" /><path d="M19.14 19.14l2.36 2.36" strokeLinecap="round"/>
                    <path d="M8 12s.5 1.5 4 1.5S16 12 16 12" strokeLinecap="round" /><circle cx="9" cy="10.5" r="1" fill="currentColor" /><circle cx="15" cy="10.5" r="1" fill="currentColor" />
                  </svg>
                ),
                title: "Complete Your Intake",
                desc: "Answer a few quick questions — your contact info, where it happened, what happened, and a few simple selections. Done in under two minutes.",
                note: null,
              },
              {
                num: "02",
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-primary">
                    <path d="M9 12h6M9 16h4M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ),
                title: "We Build Your Case",
                desc: "Our AI analyzes your situation, identifies the strongest legal angles, and prepares a detailed case summary — so any attorney who reviews it already knows exactly how to help you.",
                note: null,
              },
              {
                num: "03",
                icon: <Phone className="w-6 h-6 text-primary" />,
                title: "Expect a Call",
                desc: "Qualified attorneys in your area review your case and reach out directly. No middlemen, no waiting rooms — a real attorney will contact you.",
                note: "Expect a call within 48 hours.",
              },
            ].map((step, i) => (
              <div
                key={i}
                className={`relative glass rounded-2xl p-6 sm:p-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${400 + i * 120}ms` }}
                data-testid={`how-step-${i + 1}`}
              >
                <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full bg-background border border-primary/20 text-xs font-bold text-primary/60 font-mono">{step.num}</div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 mt-2">
                  {step.icon}
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                {step.note && (
                  <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
                    <span className="relative flex h-2 w-2 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                    </span>
                    <span className="text-xs font-medium text-primary">{step.note}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={`text-center mt-12 sm:mt-16 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              onClick={handleGatedNav}
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition-all shadow-[0_0_30px_-8px_rgba(99,102,241,0.5)] active:scale-[0.98]"
              data-testid="how-it-works-cta"
            >
              {consented ? "Start Your Free Intake" : "Get Started — Free"} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-xs text-muted-foreground">
              {consented ? "Free · Confidential · No commitment" : "Check the box in the card above to begin"}
            </p>
          </div>
        </div>
      </section>

      {/* ── Trust Section ── */}
      <section className="relative z-10 border-t border-border/20 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium border border-primary/20 bg-primary/5 text-primary mb-4">Why People Trust Us</span>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Built to Protect You First</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">We built this for people who needed legal help and couldn't find it. Every decision we made was to protect you.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <BadgeCheck className="w-6 h-6 text-primary" />,
                title: "Attorney-Verified Network",
                desc: "Every attorney on our platform is verified and licensed. You'll only hear from qualified legal professionals who practice in your case area and your state.",
              },
              {
                icon: <Shield className="w-6 h-6 text-primary" />,
                title: "No Spam Guarantee",
                desc: "We don't sell your data or share it with advertisers. Attorneys reach out through the platform — you stay in control of every contact.",
              },
              {
                icon: <Lock className="w-6 h-6 text-primary" />,
                title: "Free Forever",
                desc: "This service is 100% free for people seeking legal help. Attorneys fund the platform — you never pay, now or ever.",
              },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="glass rounded-2xl p-6 sm:p-8 flex flex-col gap-4 hover:bg-white/[0.03] transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  {icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold mb-1.5" style={{ fontFamily: 'var(--heading-font)' }}>{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative z-10 border-t border-border/20 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium border border-primary/20 bg-primary/5 text-primary mb-4">FAQ</span>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Common Questions</h2>
            <p className="mt-2 text-sm text-muted-foreground">Everything you need to know before you begin.</p>
          </div>

          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="glass rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
                >
                  <span className="text-sm font-medium text-foreground">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-primary flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="faq-body px-5 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed border-t border-border/20 pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="text-center mt-14">
            <button
              onClick={handleGatedNav}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-primary text-primary-foreground font-semibold text-base hover:brightness-110 transition-all shadow-[0_0_40px_-8px_rgba(99,102,241,0.6)] active:scale-[0.98]"
            >
              {consented ? "Start My Free Intake" : "Get Started — Free"} <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-3 text-xs text-muted-foreground">Free · Encrypted · No commitment required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/20 py-6 text-center">
        <p className="text-xs text-muted-foreground/40">© {new Date().getFullYear()} ICantFindALawyer. Not a law firm. No attorney-client relationship is formed through this platform.</p>
      </footer>

      {/* ── Mobile Sticky Bar ── */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-border/30 px-4 py-3 transition-all duration-300 ${
          showSticky ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
            <div className="relative">
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="absolute inset-0 w-5 h-5 opacity-0 cursor-pointer z-10"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${consented ? 'bg-primary border-primary' : 'border-muted-foreground/40 bg-transparent'}`}>
                {consented && <CheckCircle className="w-3 h-3 text-primary-foreground" />}
              </div>
            </div>
            <span className="text-[11px] text-muted-foreground leading-tight whitespace-nowrap">
              Agree to{" "}
              <Link to="/terms" className="text-primary" onClick={e => e.stopPropagation()}>Terms</Link>
            </span>
          </label>
          <button
            onClick={handleGatedNav}
            className={`flex-1 h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
              consented
                ? 'bg-primary text-primary-foreground hover:brightness-110 active:scale-[0.98]'
                : 'bg-secondary/60 text-muted-foreground'
            }`}
          >
            {consented
              ? <><span>Start Free Intake</span><ArrowRight className="w-4 h-4" /></>
              : <span>Check box to begin</span>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
